import { Children, createContext, type ReactNode, useContext } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { FloatingConversation } from '@/components/desktop-shell/floating-conversation';
import {
  LibraryPageLayer,
  useLibraryPageFocus,
} from '@/components/library-page/library-page-layer';
import type { ConversationLayoutNode } from '@/lib/conversations/conversation-layout';
import { useLibraryPage } from '@/lib/navigation';

export * from '@/lib/conversations/conversation-layout';

function ConversationPanelLayout({
  children,
  node,
}: {
  children: ReactNode;
  node: ConversationLayoutNode;
}) {
  if (node.kind === 'pane') {
    return (
      <ConversationPaneIdContext.Provider value={node.id}>
        {children}
      </ConversationPaneIdContext.Provider>
    );
  }
  return <ConversationSplitLayout node={node}>{children}</ConversationSplitLayout>;
}

function ConversationSplitLayout({
  children,
  node,
}: {
  children: ReactNode;
  node: Extract<ConversationLayoutNode, { kind: 'split' }>;
}) {
  const { orientation } = node;
  return (
    <Group className="h-full min-h-0 min-w-0 flex-1" id={node.id} orientation={orientation}>
      <Panel id={`${node.id}-first`} minSize={240}>
        <ConversationPanelLayout node={node.first}>{children}</ConversationPanelLayout>
      </Panel>
      <Separator
        className={`relative z-20 shrink-0 bg-foreground/15 outline-none transition-colors before:absolute before:inset-[-5px] hover:bg-foreground/35 focus-visible:bg-primary/70 data-[separator=active]:bg-primary/70 ${
          orientation === 'horizontal' ? 'w-0.5 cursor-col-resize' : 'h-0.5 cursor-row-resize'
        }`}
      />
      <Panel id={`${node.id}-second`} minSize={240}>
        <ConversationPanelLayout node={node.second}>{children}</ConversationPanelLayout>
      </Panel>
    </Group>
  );
}

const ConversationPaneIdContext = createContext<string | null>(null);

export function ConversationPaneSlot({
  children,
  paneId,
}: {
  children: ReactNode;
  paneId: string;
}) {
  return useContext(ConversationPaneIdContext) === paneId ? children : null;
}

/**
 * The conversation (the window's `main`) and its side panel; a library page covers both, keeping
 * them mounted but inert (out of the keyboard's reach and of the accessibility tree: the page is
 * the window's `main` then), and gets the focus while it is open.
 */
export function WorkspaceDockLayout({ children }: { children: ReactNode }) {
  const [workspace, sidePanel] = Children.toArray(children);
  const covered = useLibraryPage().open;
  useLibraryPageFocus();
  return (
    // Its own stacking context: its layers (a library page, a floating conversation) stay under
    // the shell sidebar, the phone's drawer included.
    <div className="relative isolate flex h-full min-h-0 min-w-0 flex-1 flex-row [container-type:inline-size]">
      <div className="contents" data-covered-workspace={covered || undefined} inert={covered}>
        <FloatingConversation>{workspace}</FloatingConversation>
        {sidePanel}
      </div>
      <LibraryPageLayer />
    </div>
  );
}

export function ConversationPanelRoot({
  children,
  node,
}: {
  children: ReactNode;
  node: ConversationLayoutNode;
}) {
  return <ConversationPanelLayout node={node}>{children}</ConversationPanelLayout>;
}
